<template>
    <div class="px-4 py-5 sm:p-6">
        <ValidationObserver v-slot="{ valid }">
            <form>
                <div>
                    <div>
                        <div v-show="hasBackendErrors($page.errors)" class="rounded-md bg-red-50 p-4 mt-4">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path fill-rule="evenodd"
                                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                              clip-rule="evenodd"/>
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <h3 class="text-sm leading-5 font-medium text-red-800">
                                        {{ trans('There were errors with your submission') }}
                                    </h3>
                                    <div class="mt-2 text-sm leading-5 text-red-700">
                                        <ul v-for="errors in $page.errors" class="list-disc pl-5">
                                            <li v-for="error in errors">{{ error }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <!-- Title -->
                            <div class="sm:col-span-4">
                                <FormLabel labelFor="title" required>{{ trans('Job title') }}</FormLabel>
                                <ValidationProvider name="form.title" rules="required" v-slot="{ errors }">
                                    <div class="mt-1">
                                        <InputText id="title" v-model="form.title" />
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                                <p class="mt-2 text-sm text-gray-500">{{ trans('Example: “Senior PHP Developer”. Title must describe one position.') }}</p>
                            </div>
                            <!-- Category -->
                            <div class="sm:col-span-4" v-if="$page.board.categories.length">
                                <FormLabel labelFor="category" required>{{ trans('Category') }}</FormLabel>
                                <ValidationProvider name="form.category_id" rules="required"
                                                    v-slot="{ errors }">
                                    <div class="mt-1">
                                    <multiselect v-model="form.categories" :options="$page.board.categories" :multiple="true" label="name" track-by="name" :close-on-select="false" :clear-on-select="false" />
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                            </div>
                            <!-- Type -->
                            <!-- Type -->
                            <div class="flex sm:flex-row flex-col sm:space-x-8 sm:col-span-6">
                                <div v-for="type in $page.board.job_types" class="flex items-center mb-4 sm:mb-0">
                                    <input :id="`job_type_full_${type.id}`"
                                           v-model="form.type"
                                           :value="type.id"
                                           name="job_type"
                                           type="radio" checked
                                           class="form-radio h-6 w-6 transition duration-150 ease-in-out"/>
                                    <label :for="`job_type_full_${type.id}`" class="ml-3"><span
                                        class="block text-sm leading-5 font-medium text-gray-700">{{ trans(type.name) }}</span></label>
                                </div>
                            </div>
                            <!-- Description -->
                            <div class="sm:col-span-6">
                                <FormLabel labelFor="description" required>{{ trans('Job description') }}</FormLabel>
                                <div class="mt-4">
                                    <ValidationProvider name="form.description" rules="required"
                                                        v-slot="{ errors }">
                                        <quill v-model="form.description" :config="editor.config" output="html"></quill>
                                        <FormError :errors="errors"/>
                                    </ValidationProvider>
                                </div>
                            </div>
                            <!-- LOCATION -->
                            <div class="sm:col-span-3">
                                <FormLabel labelFor="location" required>{{ trans('Location (or Company HQ)') }}
                                </FormLabel>
                                <ValidationProvider name="form.location_name" rules="required"
                                                    v-slot="{ errors }">
                                    <div class="mt-1">
                                        <InputText id="location" v-model="form.location_name" />
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                            </div>
                            <div class="sm:col-span-3">
                                <div class="sm:mt-8 relative flex items-center sm:justify-around">
                                    <div class="flex items-center">
                                        <input id="location_remote" v-model="form.location_is_remote"
                                               type="checkbox"
                                               class="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out"/>
                                        <div class="pl-7 text-sm leading-5">
                                            <label for="location_remote" class="text-lg text-gray-700">{{ trans('This position is 100% remote') }}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- How to apply -->
                            <div class="sm:col-span-4">
                                <FormLabel labelFor="apply" required>{{ trans('How to apply?') }}</FormLabel>
                                <ValidationProvider name="form.application" rules="required"
                                                    v-slot="{ errors }">
                                    <div class="mt-1">
                                        <InputText id="apply" v-model="form.application"/>
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                                <p class="mt-2 text-sm text-gray-500">{{ trans('Link to Application page or Email address.') }}</p>
                            </div>
                            <!-- Experience level -->
                            <div class="sm:col-span-3">
                                <FormLabel labelFor="experience">{{ trans('Experience') }}
                                </FormLabel>
                                <div class="mt-1">
                                    <select id="experience" v-model="form.experience"
                                            class="block focus:ring-indigo-500 focus:border-indigo-500 w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                                        <option v-for="level in $page.board.job_experience_levels" :value="level.id">{{ level.name }}</option>
                                    </select>
                                </div>
                            </div>
                            <!-- Salary -->
                            <div class="sm:col-span-3">
                                <FormLabel labelFor="salary">{{ trans('Salary') }}</FormLabel>
                                <div class="mt-1">
                                    <InputText id="salary" v-model="form.salary" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-8 border-t border-gray-200 pt-8">
                        <div>
                            <h3 class="text-2xl leading-6 font-medium text-indigo-600">
                                {{ trans('Company Information') }}
                            </h3>
                        </div>
                        <div class="mt-12 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                            <div class="sm:col-span-3">
                                <FormLabel labelFor="company_name" required>{{ trans('Company name') }}</FormLabel>
                                <ValidationProvider name="form.company_name" rules="required"
                                                    v-slot="{ errors }">
                                    <div class="mt-1">
                                        <InputText id="company_name" v-model="form.company_name" ref="companyName" />
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                            </div>

                            <div class="sm:col-span-3">
                                <FormLabel labelFor="company_url" required>{{ trans('Company website URL') }}
                                </FormLabel>
                                <ValidationProvider name="form.company_url" rules="required"
                                                    v-slot="{ errors }">
                                    <div class="mt-1 rounded-md shadow-sm">
                                        <InputText id="company_url" v-model="form.company_url" />
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                            </div>

                            <!-- Company Description -->
                            <div class="sm:col-span-6">
                                <FormLabel labelFor="company_description" required>{{ trans('About company') }}
                                </FormLabel>
                                <ValidationProvider name="form.company_description" rules="required"
                                                    v-slot="{ errors }">
                                    <div class="mt-3 rounded-md shadow-sm">
                                                    <textarea id="company_description" cols="30" rows="5"
                                                              v-model="form.company_description"
                                                              class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <FormError :errors="errors"/>
                                </ValidationProvider>
                            </div>
                            <!-- Description -->

                            <div class="sm:col-span-6">
                                <FormLabel labelFor="logo" required>{{ trans('Logo') }}</FormLabel>
                                <FormUpload v-on:file-selected="handleFile($event)"
                                            :existing="form.company_existing_logo_absolute"></FormUpload>
                            </div>

                        </div>
                    </div>
                </div>
                <div class="mt-8 border-t border-gray-200 pt-5">
                    <div class="flex" :class="!job.published_at ? 'justify-between': 'justify-end'">
                        <span v-if="!job.published_at" class="inline-flex rounded-md shadow-sm">
                            <inertia-link preserve-scroll method="post" :href="route('publisher.jobs.publish', [job.display_id])"
                                          :data="{ publish: true, _method: 'patch' }"
                                    class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white
                                    focus:outline-none focus:border-orange-700 focus:ring-orange transition duration-150 ease-in-out
                                    bg-orange-600 hover:bg-orange-500 active:bg-orange-700"
                            >{{ trans('Publish') }}</inertia-link>
                        </span>
                        <span class="inline-flex rounded-md shadow-sm">
                            <button @click.prevent="handleUpdate()" type="submit"
                                    class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white
                                    focus:outline-none focus:border-indigo-700 focus:ring-indigo transition duration-150 ease-in-out"
                                    :class="valid ? 'bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700' : 'bg-gray-300 bg-gray-200'"
                            >{{ trans('Save') }}</button>
                        </span>
                    </div>
                </div>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
    import FormLabel from "@/Jobroll/FormLabel";
    import FormError from "@/Jobroll/FormError";
    import FormUpload from "@/Jobroll/FormUpload";
    import InputText from "@/Jobroll/InputText";

    import {ValidationProvider, ValidationObserver} from 'vee-validate';
    import {extend} from 'vee-validate';
    import {required, email} from 'vee-validate/dist/rules';
    import Multiselect from 'vue-multiselect'

    extend('required', {
        ...required,
        message: 'This field is required'
    });
    extend('email', {
        ...email,
        message: 'We need a valid email'
    });

    export default {
        name: "JobEditForm",
        components: {
            ValidationProvider,
            ValidationObserver,
            FormLabel,
            FormError,
            FormUpload,
            Multiselect,
            InputText
        },
        props: {
            lang: {
                type: Object | String,
                default: function () {
                    return {};
                }
            },
            hasCategories: Boolean,
            job: Object
        },
        data() {
            return {
                editor: {
                    config: {
                        modules: {
                            toolbar: [
                                [{ header: [2, 3, false] }],
                                ['bold', 'italic', 'underline'],
                                ['code-block', 'link'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            ]
                        },
                        placeholder: 'Be creative ...',
                        theme: 'snow'
                    }
                },
                form: {
                    title: this.job.title,
                    categories: this.job.categories,
                    type: this.job.type,
                    salary: this.job.salary,
                    experience: this.job.experience,
                    description: this.job.description,
                    location_name: this.job.location_name,
                    location_is_remote: this.job.location_is_remote,
                    application: this.job.application_url || this.job.application_email,
                    company_email: this.job.company.email,
                    company_name: this.job.company.name,
                    company_url: this.job.company.url,
                    company_description: this.job.company.description,
                    company_existing_logo: this.job.company.logo,
                    company_existing_logo_absolute: this.job.company.logoAbsolute,
                    company_uploaded_logo: null,
                    company_uploaded_logo_src: null,
                }
            }
        },
        methods: {
            trans(value) {
                if (Object.keys(this.lang).length === 0) {
                    return value;
                }

                return this.lang[value] || value;
            },
            submit() {
                let data = new FormData();
                data.append('title', this.form.title || '');
                data.append('description', this.form.description || '');
                this.form.categories.forEach(function(el) {
                    data.append('categories[]', el.id);
                });
                if (this.form.experience) {
                    data.append('experience', this.form.experience);
                }
                if (this.form.salary) {
                    data.append('salary', this.form.salary);
                }
                data.append('type', this.form.type || '');
                data.append('application', this.form.application || '');
                data.append('location_name', this.form.location_name || '');
                data.append('location_is_remote', this.form.location_is_remote || '');
                data.append('company_name', this.form.company_name || '');
                data.append('company_description', this.form.company_description || '');
                data.append('company_url', this.form.company_url || '');
                data.append('company_email', this.form.company_email || '');
                data.append('company_existing_logo', this.form.company_existing_logo || '');
                data.append('company_uploaded_logo', this.form.company_uploaded_logo || '');

                this.$inertia.post(`/jobs/${this.job.display_id}`, data);
            },
            handleFile(event) {
                this.form.company_uploaded_logo = event[0];
                this.form.company_uploaded_logo_src = event[1];
                this.form.company_existing_logo = null;
            },
            handleUpdate() {
                this.submit();
            },
            hasBackendErrors(errors) {
                return Object.keys(errors).length > 0 && errors.constructor === Object
            }
        }
    }
</script>
<style>
@import "https://cdn.quilljs.com/1.2.6/quill.snow.css";
@import "https://unpkg.com/vue-multiselect@2.1.0/dist/vue-multiselect.min.css";
</style>
